import { useEffect, useRef } from 'react'

export default function ParticlesBackground() {
  const canvasRef = useRef(null)
  const particlesRef = useRef([])
  const mouseRef = useRef({ x: 0, y: 0 })
  const animationRef = useRef(null)
  const timeRef = useRef(0)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    
    const resizeCanvas = () => {
      canvas.width = window.innerWidth
      canvas.height = window.innerHeight
    }
    
    resizeCanvas()
    window.addEventListener('resize', resizeCanvas)

    // Classe Particule IA avancée
    class AIParticle {
      constructor() {
        this.reset()
        this.trail = []
        this.maxTrailLength = 8
        this.connections = []
        this.energy = Math.random() * 100 + 50
        this.maxEnergy = this.energy
      }

      reset() {
        this.x = Math.random() * canvas.width
        this.y = Math.random() * canvas.height
        this.vx = (Math.random() - 0.5) * 0.8
        this.vy = (Math.random() - 0.5) * 0.8
        this.size = Math.random() * 2 + 1
        this.baseSize = this.size
        this.hue = Math.random() * 60 + 200 // Bleu à violet
        this.saturation = Math.random() * 30 + 70
        this.lightness = Math.random() * 30 + 50
        this.alpha = Math.random() * 0.6 + 0.3
        this.phase = Math.random() * Math.PI * 2
        this.frequency = Math.random() * 0.02 + 0.01
        this.attractionRadius = 100 + Math.random() * 50
        this.type = Math.random() < 0.3 ? 'core' : 'satellite'
      }

      update(time, allParticles) {
        // Mouvement de base
        this.x += this.vx
        this.y += this.vy

        // Interaction avec la souris (attraction/répulsion intelligente)
        const dx = mouseRef.current.x - this.x
        const dy = mouseRef.current.y - this.y
        const distance = Math.sqrt(dx * dx + dy * dy)
        
        if (distance < this.attractionRadius) {
          const force = (this.attractionRadius - distance) / this.attractionRadius
          const angle = Math.atan2(dy, dx)
          
          if (this.type === 'core') {
            // Les particules core sont attirées
            this.vx += Math.cos(angle) * force * 0.02
            this.vy += Math.sin(angle) * force * 0.02
          } else {
            // Les satellites sont repoussées
            this.vx -= Math.cos(angle) * force * 0.015
            this.vy -= Math.sin(angle) * force * 0.015
          }
          
          this.energy = Math.min(this.maxEnergy * 1.5, this.energy + force * 20)
        }

        // Pulsation énergétique
        const pulse = Math.sin(time * this.frequency + this.phase)
        this.size = this.baseSize + pulse * 0.5 + (this.energy / this.maxEnergy) * 0.8
        this.alpha = 0.3 + pulse * 0.2 + (this.energy / this.maxEnergy) * 0.4

        // Décroissance de l'énergie
        this.energy *= 0.995

        // Limites avec téléportation fluide
        if (this.x < -10) this.x = canvas.width + 10
        if (this.x > canvas.width + 10) this.x = -10
        if (this.y < -10) this.y = canvas.height + 10
        if (this.y > canvas.height + 10) this.y = -10

        // Friction intelligente
        this.vx *= 0.995
        this.vy *= 0.995

        // Ajouter à la traînée
        this.trail.push({ x: this.x, y: this.y, alpha: this.alpha * 0.5 })
        if (this.trail.length > this.maxTrailLength) {
          this.trail.shift()
        }

        // Chercher des connexions avec d'autres particules
        this.connections = []
        allParticles.forEach(other => {
          if (other !== this) {
            const dx = other.x - this.x
            const dy = other.y - this.y
            const dist = Math.sqrt(dx * dx + dy * dy)
            
            if (dist < 120 && dist > 20) {
              const strength = (120 - dist) / 120
              this.connections.push({
                particle: other,
                strength: strength,
                distance: dist
              })
            }
          }
        })
      }

      draw(ctx, time) {
        ctx.save()
        
        // Dessiner la traînée
        this.trail.forEach((point, index) => {
          const trailAlpha = (index / this.trail.length) * point.alpha * 0.6
          const trailSize = this.size * (index / this.trail.length) * 0.5
          
          const gradient = ctx.createRadialGradient(
            point.x, point.y, 0,
            point.x, point.y, trailSize * 2
          )
          gradient.addColorStop(0, `hsla(${this.hue}, ${this.saturation}%, ${this.lightness + 20}%, ${trailAlpha})`)
          gradient.addColorStop(1, `hsla(${this.hue}, ${this.saturation}%, ${this.lightness}%, 0)`)
          
          ctx.fillStyle = gradient
          ctx.beginPath()
          ctx.arc(point.x, point.y, trailSize, 0, Math.PI * 2)
          ctx.fill()
        })

        // Dessiner les connexions
        this.connections.forEach(conn => {
          const alpha = conn.strength * this.alpha * conn.particle.alpha * 0.3
          ctx.strokeStyle = `hsla(${(this.hue + conn.particle.hue) / 2}, 60%, 70%, ${alpha})`
          ctx.lineWidth = conn.strength * 2
          ctx.beginPath()
          ctx.moveTo(this.x, this.y)
          ctx.lineTo(conn.particle.x, conn.particle.y)
          ctx.stroke()
          
          // Point de connexion au milieu
          const midX = (this.x + conn.particle.x) / 2
          const midY = (this.y + conn.particle.y) / 2
          const midGradient = ctx.createRadialGradient(midX, midY, 0, midX, midY, 3)
          midGradient.addColorStop(0, `hsla(${this.hue}, 80%, 80%, ${alpha * 2})`)
          midGradient.addColorStop(1, 'transparent')
          
          ctx.fillStyle = midGradient
          ctx.beginPath()
          ctx.arc(midX, midY, 2, 0, Math.PI * 2)
          ctx.fill()
        })
        
        // Particule principale avec effet glow multiple
        const glowSize = this.size * 4
        const coreGradient = ctx.createRadialGradient(
          this.x, this.y, 0,
          this.x, this.y, glowSize
        )
        
        // Glow externe
        coreGradient.addColorStop(0, `hsla(${this.hue}, ${this.saturation}%, ${this.lightness + 30}%, ${this.alpha})`)
        coreGradient.addColorStop(0.4, `hsla(${this.hue}, ${this.saturation}%, ${this.lightness}%, ${this.alpha * 0.6})`)
        coreGradient.addColorStop(1, 'transparent')
        
        ctx.fillStyle = coreGradient
        ctx.beginPath()
        ctx.arc(this.x, this.y, glowSize, 0, Math.PI * 2)
        ctx.fill()
        
        // Core de la particule
        const coreSize = this.size * (this.type === 'core' ? 1.5 : 1)
        const particleGradient = ctx.createRadialGradient(
          this.x, this.y, 0,
          this.x, this.y, coreSize
        )
        particleGradient.addColorStop(0, `hsla(${this.hue}, 90%, 90%, ${this.alpha})`)
        particleGradient.addColorStop(0.7, `hsla(${this.hue}, ${this.saturation}%, ${this.lightness}%, ${this.alpha * 0.8})`)
        particleGradient.addColorStop(1, 'transparent')
        
        ctx.fillStyle = particleGradient
        ctx.beginPath()
        ctx.arc(this.x, this.y, coreSize, 0, Math.PI * 2)
        ctx.fill()
        
        // Brillance centrale
        if (this.energy > this.maxEnergy * 0.7) {
          const sparkleGradient = ctx.createRadialGradient(
            this.x - this.size * 0.3, this.y - this.size * 0.3, 0,
            this.x, this.y, this.size * 0.8
          )
          sparkleGradient.addColorStop(0, `hsla(${this.hue}, 100%, 95%, ${this.alpha * 0.8})`)
          sparkleGradient.addColorStop(1, 'transparent')
          
          ctx.fillStyle = sparkleGradient
          ctx.beginPath()
          ctx.arc(this.x - this.size * 0.2, this.y - this.size * 0.2, this.size * 0.6, 0, Math.PI * 2)
          ctx.fill()
        }
        
        ctx.restore()
      }
    }

    // Initialiser les particules IA
    const initParticles = () => {
      particlesRef.current = []
      const particleCount = Math.min(Math.floor((canvas.width * canvas.height) / 12000), 60)
      
      for (let i = 0; i < particleCount; i++) {
        particlesRef.current.push(new AIParticle())
      }
    }

    // Animation principale
    const animate = () => {
      timeRef.current += 0.016 // ~60fps
      
      // Fond avec effet de fade intelligent
      ctx.fillStyle = 'rgba(26, 26, 46, 0.03)'
      ctx.fillRect(0, 0, canvas.width, canvas.height)

      // Dessiner grille énergétique subtile
      if (timeRef.current % 2 < 0.016) { // Toutes les 2 secondes
        ctx.strokeStyle = 'rgba(69, 202, 255, 0.05)'
        ctx.lineWidth = 0.5
        const gridSize = 100
        
        for (let x = 0; x < canvas.width; x += gridSize) {
          ctx.beginPath()
          ctx.moveTo(x, 0)
          ctx.lineTo(x, canvas.height)
          ctx.stroke()
        }
        
        for (let y = 0; y < canvas.height; y += gridSize) {
          ctx.beginPath()
          ctx.moveTo(0, y)
          ctx.lineTo(canvas.width, y)
          ctx.stroke()
        }
      }

      // Mettre à jour et dessiner les particules
      particlesRef.current.forEach(particle => {
        particle.update(timeRef.current, particlesRef.current)
        particle.draw(ctx, timeRef.current)
      })

      animationRef.current = requestAnimationFrame(animate)
    }

    // Gestion de la souris
    const handleMouseMove = (e) => {
      mouseRef.current.x = e.clientX
      mouseRef.current.y = e.clientY
    }

    const handleMouseLeave = () => {
      mouseRef.current.x = -1000
      mouseRef.current.y = -1000
    }

    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseleave', handleMouseLeave)
    
    initParticles()
    animate()

    return () => {
      window.removeEventListener('resize', resizeCanvas)
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseleave', handleMouseLeave)
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
    }
  }, [])

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 w-full h-full pointer-events-none z-0"
      style={{
        background: 'linear-gradient(135deg, #0f0f1e 0%, #1a1a2e 25%, #16213e 50%, #0f3460 100%)',
        opacity: 0.9
      }}
    />
  )
}